/**
 * The lazy boundary in front of the composer.
 *
 * The composer is the only screen that needs CodeMirror — its state, view,
 * language, autocomplete, lint and commands packages — and the DSL. Behind
 * `lazy` + `Suspense` all of it moves into its own chunk, fetched the first
 * time someone opens a Deal Model's Editor area; the wasm engine loads the same
 * way and is a far larger asset. Nothing but this loader may import
 * `./DealModelEditor` statically, or the chunk collapses back into the entry.
 */
import { Suspense, lazy, type ReactNode } from 'react'
import type { DealModelSummary } from '../../api/dealModels'
import { Shell } from '../../app/Shell'
import { Skeletons } from '../../ui/kit'
import { WorkspaceHead } from './WorkspaceHead'

const DealModelComposer = lazy(async () => {
  const composer = await import('./DealModelEditor')
  return { default: composer.DealModelEditor }
})

export function DealModelEditorArea({ model, notice }: { model: DealModelSummary; notice: ReactNode }) {
  return (
    <Suspense fallback={
      <Shell head={<WorkspaceHead model={model} area="editor" />} fill status={<span>Loading the composer</span>}>
        <div className="dm-composer" data-testid="deal-model-editor-loading"><div className="dm-loading"><Skeletons count={1} kind="block" /><Skeletons count={6} kind="row" /></div></div>
      </Shell>
    }>
      <DealModelComposer key={model.logicalModelId} model={model} notice={notice} />
    </Suspense>
  )
}
